import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import uniqBy from 'lodash.uniqby';

import { useRestaurantsService } from '@restworld/data-services';

import {
  explorerRestaurantMapLoadingAtom,
  explorerRestaurantListAtom,
  explorerRestaurantHitsAtom,
  explorerRestaurantListPayloadAtom,
  explorerRestaurantMapMarkersAtom,
  restaurantHasLimitReachedAtom,
  explorerRestaurantListLoadingAtom,
} from '../job-explorer-map';
import { defaultJobListPayload } from '../job-explorer.types';
import { useResponsiveBreakPoints } from '@restworld/utils-common';
import { hasMapViewAtom } from '../job-explorer-list.atoms';

export const useExplorerRestaurantQueryKey = () => {
  const explorerRestaurantListPayload = useAtomValue(explorerRestaurantListPayloadAtom);
  return useMemo(() => ['fetchExplorerRestaurantList', explorerRestaurantListPayload], [explorerRestaurantListPayload]);
};

export const useRestaurantLocationCoordinates = () => {
  const restaurantService = useRestaurantsService();
  const setExplorerRestaurantsMapMarkers = useSetAtom(explorerRestaurantMapMarkersAtom);
  const setExplorerRestaurantMapLoading = useSetAtom(explorerRestaurantMapLoadingAtom);

  useQuery({
    queryKey: ['fetchRestaurantLocationCoordinates'],
    queryFn: async () => {
      setExplorerRestaurantMapLoading(true);
      const data = (await restaurantService.getRestaurantLocationCoordinates()).data;
      setExplorerRestaurantMapLoading(false);
      setExplorerRestaurantsMapMarkers(data ?? []);
      return data ?? [];
    },
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });
};

export const useFetchExplorerRestaurantList = () => {
  const restaurantService = useRestaurantsService();
  const setExplorerRestaurantsList = useSetAtom(explorerRestaurantListAtom);
  const setExplorerRestaurantHits = useSetAtom(explorerRestaurantHitsAtom);
  const setExplorerRestaurantListLoading = useSetAtom(explorerRestaurantListLoadingAtom);
  const explorerRestaurantListPayload = useAtomValue(explorerRestaurantListPayloadAtom);
  const setRestaurantHasLimitReached = useSetAtom(restaurantHasLimitReachedAtom);

  const callRestaurantListApi = useCallback(
    async (axiosConfig?: AxiosRequestConfig) => {
      setExplorerRestaurantListLoading(true);
      const data = (
        await restaurantService.fetchExploreRestaurantsList(
          {
            ...(explorerRestaurantListPayload ?? defaultJobListPayload),
          },
          { ...axiosConfig }
        )
      ).data?.data?.explore_restaurants;
      setExplorerRestaurantListLoading(false);

      const restaurants = data?.data.map((v) => v.restaurant) ?? [];
      setExplorerRestaurantsList((prev) =>
        explorerRestaurantListPayload.offset > 0 ? uniqBy([...prev, ...restaurants], 'id') : [...restaurants]
      );
      setRestaurantHasLimitReached(restaurants.length === 0);
      setExplorerRestaurantHits(data?.hits ?? 0);
      return data ?? [];
    },
    [
      setExplorerRestaurantListLoading,
      restaurantService,
      explorerRestaurantListPayload,
      setExplorerRestaurantsList,
      setRestaurantHasLimitReached,
      setExplorerRestaurantHits,
    ]
  );

  return { callRestaurantListApi };
};

export const useRQueryFetchExplorerRestaurantList = () => {
  const hasMapView = useAtomValue(hasMapViewAtom);
  const { isDesktop } = useResponsiveBreakPoints();
  const { callRestaurantListApi } = useFetchExplorerRestaurantList();
  const queryKey = useExplorerRestaurantQueryKey();

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => callRestaurantListApi({ signal }),
    placeholderData: (previousData) => previousData,
    enabled: !isDesktop ? !hasMapView : true,
  });
};
